import { HiOutlineCheck, HiOutlineGift } from "react-icons/hi";
import { translate } from "ui/i18n";
import { HorizontalDivider } from "ui/pages/app/components/HorizontalDivider";
import { useWindowWidth } from "ui/utils/hooks";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalSubscriptionPricing } from "ui/pages/app/account/components/ModalSubscriptionPricing";
import { useDisclosure } from "@chakra-ui/hooks";

export function ChooseAccountType({ register }: { register?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const modalPricingState = useDisclosure();
  const email = location.state?.email;

  useEffect(() => {
    if (!email) navigate("/auth");
  }, [email]); //eslint-disable-line

  const handleFreePlanClick = () => {
    navigate("/choice-confirmation", { state: { email } });
  };

  const handlePremiumPlanClick = () => {
    modalPricingState.onOpen();
  };

  const handleProPlanClick = () => {
    navigate("/send-quote-request", { state: { email } });
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        {translate("ns1:ChooseAccountType.ChooseYourPlan")}
      </h2>
      <div className="mt-10 grid w-full max-w-[1200px] grid-cols-1 gap-4 md:mt-20 md:grid-cols-3 md:gap-10">
        <GridColumn
          plan="Free"
          functionnalitiesCount={4}
          onClick={handleFreePlanClick}
        />
        <GridColumn
          plan="Premium"
          functionnalitiesCount={5}
          onClick={handlePremiumPlanClick}
        />
        <GridColumn
          plan="Pro"
          functionnalitiesCount={6}
          onClick={handleProPlanClick}
        />
      </div>
      <ModalSubscriptionPricing
        isOpen={modalPricingState.isOpen}
        onClose={modalPricingState.onClose}
        email={email}
        register={register}
      />
    </div>
  );
}

function GridColumn({
  plan,
  functionnalitiesCount,
  onClick,
}: {
  plan: "Free" | "Premium" | "Pro";
  functionnalitiesCount: number;
  onClick: () => void;
}) {
  const location = useLocation();
  const isDesktop = useWindowWidth();
  const accountExists = location.state?.accountExists;

  return (
    <div className="flex flex-col justify-between rounded-3xl border border-darkGrey-700 px-4 py-6 md:px-6 md:py-8">
      <div>
        <h4 className="text-base font-bold text-darkGrey-900 md:text-lg">
          {translate(`ns1:ChooseAccountType.Plans.${plan}.Title`)}
        </h4>
        <p className="text-2xl text-xl font-bold text-darkGrey-900 md:text-2xl">
          {translate(`ns1:ChooseAccountType.Plans.${plan}.Price`)}
          {plan === "Premium" && (
            <span className="text-base font-normal md:text-lg">
              {translate("ns1:ChooseAccountType.Plans.Premium.PerMonth")}
            </span>
          )}
        </p>

        {plan === "Premium" && (
          <div className="mt-2 flex items-center text-sm font-medium text-darkGrey-900">
            <span className="me-2 rounded-[36px] bg-darkGrey-200 px-2 py-0.5 font-semibold text-secondaryGrey-800">
              {"-17%"}
            </span>
            {translate("ns1:ChooseAccountType.BySubscribingAYear")}
          </div>
        )}

        {(plan === "Premium" || plan === "Pro") && (
          <div className="text-md mt-6 flex items-center font-medium text-darkGrey-700">
            <div className="me-2 h-6 w-6">
              <HiOutlineGift color="#E14277" size={24} />
            </div>
            {translate(`ns1:ChooseAccountType.30DaysFreeTrial`)}
          </div>
        )}
        <HorizontalDivider gap={isDesktop ? "24px" : "16px"} />
        <p className="text-md font-bold text-darkGrey-700">
          {translate("ns1:ChooseAccountType.WhatsIncluded")}
        </p>

        <div className="mt-4 space-y-4">
          {[...Array(functionnalitiesCount)].map((_, index) => (
            <div
              className="text-md flex items-center font-medium text-darkGrey-700"
              key={index}
            >
              <div className="me-2 h-6 w-6">
                <HiOutlineCheck color="#E14277" size={24} />
              </div>
              {translate(
                `ns1:ChooseAccountType.Plans.${plan}.Func${
                  (index + 1) as 1 | 2 | 3 | 4 | 5 | 6
                }`
              )}
            </div>
          ))}
        </div>
      </div>

      {accountExists && plan === "Free" ? (
        <PrimaryButton
          height="46px"
          width="100%"
          className="mt-6"
          disabled
          outlined
        >
          {translate("ns1:ChooseAccountType.YourCurrentPlan")}
        </PrimaryButton>
      ) : (
        <PrimaryButton
          height="46px"
          width="100%"
          onClick={onClick}
          className="mt-6"
        >
          {translate(
            `ns1:ChooseAccountType.${
              plan === "Pro" ? "TakeContact" : "ChooseThisPlan"
            }`
          )}
        </PrimaryButton>
      )}
    </div>
  );
}
